import { useEffect, useRef } from 'react';
import { hydrateCtaLinks } from '../../../shared-web/cta-targets';

/**
 * Renders an author-supplied page layout.
 *
 * Used by /for/<slug> and /blog/<slug> when the row carries `page_html`. The
 * built-in layouts stay as the fallback for rows that do not.
 *
 * The HTML is injected. It arrives already templated, sanitised and CSS-scoped
 * by src/edge/content/page-html.js — a positive tag/attribute allowlist that
 * cannot emit a script, a form, an event handler, or a request to another host.
 * That pipeline is the only reason this is acceptable: these pages render on
 * the origin that holds a signed-in visitor's session cookie. Do not set this
 * prop from anywhere that has not been through it.
 *
 * `.ember-page-html` is not decoration — it is the selector the author's CSS
 * was scoped to, so removing or renaming it silently unstyles every authored
 * page. It carries no styling of its own on purpose: the whole point is that
 * the page decides how it looks.
 */
export default function AuthoredPage({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);

  // `ember:` links were resolved server-side into real hrefs with a marker.
  // This is the pass that adds the attribution the server could not compute.
  useEffect(() => {
    hydrateCtaLinks(ref.current);
  }, [html]);

  return (
    <div
      ref={ref}
      className="ember-page-html flex-1"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
